import {
  t,
  type WireMission,
  type SectorCoord,
  missionsCapFor,
} from '@viral-space/shared';
import { attachMobileCloseButton, isMobileLayout } from './ui-shared';
import type { CompletedMissionEntry } from '../game/state';

const COLORS = {
  primary: '#00ff41',
  secondary: '#ffb000',
  danger: '#ff4444',
  bgSolid: 'rgba(0,8,0,0.92)',
  panelBg: 'rgba(0,12,0,0.8)',
  dim: 'rgba(0,255,65,0.3)',
} as const;

const FONT = "'Share Tech Mono', monospace";
const GLOW = `0 0 6px #00ff41, 0 0 12px rgba(0,255,65,0.3)`;
const BOX_GLOW = `0 0 8px rgba(0,255,65,0.4), inset 0 0 8px rgba(0,255,65,0.05)`;

const OBJECTIVE_KIND_LABEL: Record<number, string> = {
  0: 'KILL',
  1: 'MINE',
  2: 'EXPLORE',
  3: 'DELIVER',
  4: 'COLLECT',
  5: 'PROTECT',
};

/**
 * Player-facing journal showing every mission the pilot has accepted.
 * Opens with `J`, the in-HUD "Missions" button, or a touch shortcut.
 *
 * The panel is purely presentational — it pulls live state via the
 * `getMissions` getter every time it renders, so the parent never has
 * to push updates manually. Abandon / waypoint actions fire callbacks
 * the parent owns.
 */
type JournalTab = 'active' | 'completed';

export class MissionJournal {
  private container: HTMLElement;
  private root!: HTMLElement;
  private body!: HTMLElement;
  private slotCounter!: HTMLElement;
  private activeTabBtn!: HTMLElement;
  private completedTabBtn!: HTMLElement;
  private activeTab: JournalTab = 'active';
  private _visible = false;
  private redrawTimer: ReturnType<typeof setInterval> | null = null;

  /** Live accessor — called every render so the panel is always fresh. */
  getMissions: (() => WireMission[]) | null = null;
  /**
   * Live accessor for the locally-tracked completed-mission history.
   * Called on every render so dropping a mission via the abandon flow
   * or completing one in another tab refreshes the list immediately.
   */
  getCompletedMissions: (() => CompletedMissionEntry[]) | null = null;
  /**
   * Live accessor for the calling account's premium status. Drives the
   * "slots used" counter so a Premium subscriber sees `5 / 8` instead
   * of `5 / 5`. Defaults to "free" when not set so the cap never
   * accidentally inflates for unauthenticated UI previews.
   */
  isPremium: (() => boolean) | null = null;
  onAbandon: ((missionId: string) => void) | null = null;
  /**
   * Live accessor for "which mission id is currently pinned to the
   * radar". Returning `null` means no mission is — either because the
   * radar is empty or because it's pointing at a manual destination
   * from the universe map. Used by the "Show in Radar" toggle so each
   * card knows whether it should render in the on or off state.
   */
  getRadarMissionId: (() => string | null) | null = null;
  /**
   * Toggle "Show in Radar" for a mission. The host wires this to flip
   * navigationTarget / radarMissionId atomically. Passing `null` for
   * `sector` means "unpin this mission and clear the radar". The
   * mutual-exclusion guarantee ("only one mission shown at a time")
   * lives in the host's setNavigationTarget — this callback only
   * declares intent.
   */
  onShowInRadar: ((missionId: string, sector: SectorCoord | null) => void) | null = null;
  onClose: (() => void) | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.build();
  }

  private el(
    tag: string,
    styles: Partial<CSSStyleDeclaration>,
    text?: string,
  ): HTMLElement {
    const e = document.createElement(tag);
    Object.assign(e.style, styles);
    if (text !== undefined) e.textContent = text;
    return e;
  }

  private build(): void {
    this.root = this.el('div', {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 'min(560px, 92vw)',
      maxHeight: '88vh',
      background: COLORS.bgSolid,
      border: `1px solid ${COLORS.dim}`,
      boxShadow: BOX_GLOW,
      padding: '14px 16px',
      fontFamily: FONT,
      color: COLORS.primary,
      letterSpacing: '1px',
      textTransform: 'uppercase',
      display: 'none',
      flexDirection: 'column',
      gap: '10px',
      zIndex: '60',
      pointerEvents: 'auto',
    });
    this.container.appendChild(this.root);

    const header = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: `1px solid ${COLORS.dim}`,
      paddingBottom: '6px',
      flexShrink: '0',
    });
    const titleWrap = this.el('div', {
      display: 'flex',
      alignItems: 'baseline',
      gap: '12px',
      flexWrap: 'wrap',
    });
    titleWrap.appendChild(
      this.el('div', {
        fontSize: '16px',
        textShadow: GLOW,
        letterSpacing: '3px',
      }, t('mission.journalTitle')),
    );
    // Live capacity counter. Refreshed every render so the digits stay
    // in sync with accept / abandon / expire events.
    this.slotCounter = this.el('div', {
      fontSize: '11px',
      color: COLORS.dim,
      letterSpacing: '2px',
    });
    titleWrap.appendChild(this.slotCounter);
    header.appendChild(titleWrap);

    const closeBtn = this.el('button', {
      fontFamily: FONT,
      fontSize: '12px',
      background: 'transparent',
      border: `1px solid ${COLORS.danger}`,
      color: COLORS.danger,
      padding: '4px 10px',
      cursor: 'pointer',
      letterSpacing: '2px',
    }, 'X');
    closeBtn.addEventListener('click', () => this.hide());
    header.appendChild(closeBtn);
    this.root.appendChild(header);

    const tabBar = this.el('div', {
      display: 'flex',
      gap: '6px',
      borderBottom: `1px solid ${COLORS.dim}`,
      paddingBottom: '4px',
      flexShrink: '0',
    });
    this.activeTabBtn = this.makeTabButton(t('mission.tabActive'), 'active');
    this.completedTabBtn = this.makeTabButton(t('mission.tabCompleted'), 'completed');
    tabBar.appendChild(this.activeTabBtn);
    tabBar.appendChild(this.completedTabBtn);
    this.root.appendChild(tabBar);

    this.body = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      overflowY: 'auto',
      paddingRight: '4px',
    });
    this.root.appendChild(this.body);

    // Hide the in-header X on mobile — the helper installs a corner-
    // anchored ✕ that overlays it, and rendering both produced the
    // user-reported "two stacked close buttons" artifact on the
    // mission journal modal. Same pattern friends-panel / leaderboard
    // / universe-map / station-ui / warp-gate-ui already use.
    attachMobileCloseButton(this.root, () => this.hide(), {
      hideOnMobile: [closeBtn],
    });
    this.refreshTabStyles();
  }

  private makeTabButton(label: string, tab: JournalTab): HTMLElement {
    const btn = this.el('button', {
      fontFamily: FONT,
      fontSize: '11px',
      padding: '5px 14px',
      background: 'transparent',
      border: `1px solid ${COLORS.dim}`,
      color: COLORS.primary,
      cursor: 'pointer',
      letterSpacing: '2px',
      transition: 'all 0.15s',
    }, label);
    btn.addEventListener('click', () => {
      if (this.activeTab === tab) return;
      this.activeTab = tab;
      this.refreshTabStyles();
      this.render();
    });
    return btn;
  }

  private refreshTabStyles(): void {
    const apply = (btn: HTMLElement, selected: boolean) => {
      btn.style.background = selected ? COLORS.primary : 'transparent';
      btn.style.color = selected ? '#000' : COLORS.primary;
      btn.style.borderColor = selected ? COLORS.primary : COLORS.dim;
      btn.style.textShadow = selected ? '' : 'none';
    };
    apply(this.activeTabBtn, this.activeTab === 'active');
    apply(this.completedTabBtn, this.activeTab === 'completed');
  }

  private formatTimeLeft(expiresAt: number | undefined): string {
    if (!expiresAt) return '—';
    const ms = Math.max(0, expiresAt - Date.now());
    const totalSec = Math.floor(ms / 1000);
    const days = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec % 86400) / 3600);
    const mins = Math.floor((totalSec % 3600) / 60);
    const secs = totalSec % 60;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  }

  private buildMissionCard(m: WireMission): HTMLElement {
    const card = this.el('div', {
      border: `1px solid ${COLORS.dim}`,
      background: 'rgba(0,12,0,0.55)',
      padding: '10px 12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    });

    const head = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: '8px',
      flexWrap: 'wrap',
    });
    head.appendChild(
      this.el('div', {
        fontSize: '13px',
        textShadow: GLOW,
        letterSpacing: '2px',
        flex: '1',
        minWidth: '160px',
      }, m.title),
    );
    if (m.isWeekly) {
      head.appendChild(this.el('span', {
        fontSize: '9px',
        color: COLORS.secondary,
        border: `1px solid ${COLORS.secondary}`,
        padding: '1px 6px',
        letterSpacing: '2px',
      }, t('mission.weekly')));
    } else if (m.isDaily) {
      head.appendChild(this.el('span', {
        fontSize: '9px',
        color: COLORS.primary,
        border: `1px solid ${COLORS.primary}`,
        padding: '1px 6px',
        letterSpacing: '2px',
      }, t('mission.daily')));
    }
    card.appendChild(head);

    card.appendChild(
      this.el('div', {
        fontSize: '11px',
        color: COLORS.dim,
        textTransform: 'none',
        lineHeight: '1.35',
      }, m.description),
    );

    const objWrap = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      marginTop: '4px',
    });
    objWrap.appendChild(this.el('div', {
      fontSize: '10px',
      color: COLORS.secondary,
      letterSpacing: '2px',
    }, t('mission.objectives')));

    for (const obj of m.objectives) {
      const row = this.el('div', {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
      });
      const labelRow = this.el('div', {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '10px',
        color: obj.completed ? COLORS.primary : COLORS.dim,
      });
      const kindLabel = OBJECTIVE_KIND_LABEL[obj.type] ?? `OBJ ${obj.type}`;
      labelRow.appendChild(this.el('span', {}, `${kindLabel}${obj.target && obj.target !== '*' ? ' · ' + obj.target : ''}`));
      labelRow.appendChild(this.el('span', { textShadow: obj.completed ? GLOW : 'none' },
        `${obj.current} / ${obj.required}${obj.completed ? ' ✓' : ''}`));
      row.appendChild(labelRow);

      const barTrack = this.el('div', {
        height: '4px',
        background: 'rgba(0,255,65,0.08)',
        border: `1px solid ${COLORS.dim}`,
        position: 'relative',
      });
      const pct = obj.required > 0
        ? Math.min(100, Math.round((obj.current / obj.required) * 100))
        : 0;
      const fill = this.el('div', {
        height: '100%',
        width: `${pct}%`,
        background: obj.completed ? COLORS.primary : 'rgba(0,255,65,0.55)',
        boxShadow: obj.completed ? `0 0 6px ${COLORS.primary}` : 'none',
        transition: 'width 0.2s',
      });
      barTrack.appendChild(fill);
      row.appendChild(barTrack);
      objWrap.appendChild(row);
    }
    card.appendChild(objWrap);

    const meta = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '8px',
      fontSize: '10px',
      color: COLORS.dim,
      flexWrap: 'wrap',
      marginTop: '4px',
    });

    const rewards = this.el('div', { display: 'flex', gap: '8px' });
    rewards.appendChild(this.el('span', { color: COLORS.primary },
      t('mission.rewardXp', { n: m.rewards.xp })));
    rewards.appendChild(this.el('span', { color: COLORS.secondary },
      t('mission.rewardCredits', { n: m.rewards.credits.toLocaleString() })));
    meta.appendChild(rewards);

    meta.appendChild(this.el('span', { color: COLORS.secondary },
      `${t('mission.timeRemaining')}: ${this.formatTimeLeft(m.expiresAt)}`));

    card.appendChild(meta);

    const actions = this.el('div', {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
      marginTop: '4px',
    });

    if (m.targetSector) {
      const isOnRadar = (this.getRadarMissionId?.() ?? null) === m.id;
      // The toggle lives on a per-card basis but is mutually exclusive
      // across the journal: clicking another mission's toggle on
      // automatically swaps the radar pin to it (the host enforces
      // single-mission-at-a-time inside setNavigationTarget). Here we
      // only flip our own state and call back.
      const radarBtn = this.makeToggleBtn(
        isOnRadar ? t('mission.shownInRadar') : t('mission.showInRadar'),
        isOnRadar,
        () => {
          if (!m.targetSector) return;
          // Toggling an already-active card off clears the pin; tapping
          // a different card swaps the pin to it.
          this.onShowInRadar?.(m.id, isOnRadar ? null : m.targetSector);
          this.render();
        },
      );
      actions.appendChild(radarBtn);
    }
    const abandonBtn = this.makeBtn(t('mission.abandon'), () => {
      this.onAbandon?.(m.id);
    }, COLORS.danger);
    actions.appendChild(abandonBtn);
    card.appendChild(actions);

    return card;
  }

  /**
   * Two-state action button that visually flips between "off" (outline)
   * and "on" (filled). Used for the "Show in Radar" toggle so the
   * journal can render the radar pin's current owner inline with the
   * mission cards.
   *
   * The leading glyph (`◉` / `○`) reinforces the on/off distinction
   * for color-blind players who might not pick up the fill change at
   * a glance.
   *
   * Mobile bumps padding + font so the button comfortably clears the
   * 32–40px touch-target threshold; on desktop the button stays in
   * line with the existing dense ABANDON button so the action row
   * keeps its compact look.
   */
  private makeToggleBtn(label: string, on: boolean, onClick: () => void): HTMLElement {
    const color = COLORS.secondary;
    const glyph = on ? '◉' : '○';
    const mobile = isMobileLayout();
    const btn = this.el('button', {
      fontFamily: FONT,
      fontSize: mobile ? '12px' : '11px',
      padding: mobile ? '8px 14px' : '5px 12px',
      background: on ? color : 'transparent',
      border: `1px solid ${color}`,
      color: on ? '#000' : color,
      cursor: 'pointer',
      letterSpacing: mobile ? '1.5px' : '2px',
      transition: 'all 0.15s',
      textShadow: on ? 'none' : `0 0 4px ${color}`,
      // Block iOS double-tap zoom so a fast pilot toggling between
      // two missions doesn't zoom the whole journal mid-tap.
      touchAction: 'manipulation',
      // Stop the OS from highlighting the entire button rectangle in
      // bright blue / grey on tap — the on/off fill itself is already
      // the affordance, the system overlay just looks broken on top
      // of the green theme.
      WebkitTapHighlightColor: 'transparent',
    } as Partial<CSSStyleDeclaration>, `${glyph}  ${label}`);
    btn.addEventListener('mouseenter', () => {
      if (on) return;
      btn.style.background = `${color}33`;
    });
    btn.addEventListener('mouseleave', () => {
      if (on) return;
      btn.style.background = 'transparent';
    });
    btn.addEventListener('click', onClick);
    return btn;
  }

  private makeBtn(label: string, onClick: () => void, color: string): HTMLElement {
    const btn = this.el('button', {
      fontFamily: FONT,
      fontSize: '11px',
      padding: '5px 12px',
      background: 'transparent',
      border: `1px solid ${color}`,
      color,
      cursor: 'pointer',
      letterSpacing: '2px',
      transition: 'all 0.15s',
    }, label);
    btn.addEventListener('mouseenter', () => {
      btn.style.background = color;
      btn.style.color = '#000';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.background = 'transparent';
      btn.style.color = color;
    });
    btn.addEventListener('click', onClick);
    return btn;
  }

  private render(): void {
    this.body.innerHTML = '';

    if (this.activeTab === 'completed') {
      const completed = this.getCompletedMissions?.() ?? [];
      this.slotCounter.textContent = t('mission.completedCount', {
        n: completed.length,
      });
      this.slotCounter.style.color = COLORS.dim;
      this.slotCounter.style.textShadow = 'none';
      if (completed.length === 0) {
        this.body.appendChild(
          this.el('div', {
            color: COLORS.dim,
            textAlign: 'center',
            padding: '24px 8px',
            fontSize: '11px',
            textTransform: 'none',
            lineHeight: '1.4',
          }, t('mission.noneCompleted')),
        );
        return;
      }
      for (const c of completed) {
        this.body.appendChild(this.buildCompletedCard(c));
      }
      return;
    }

    const missions = this.getMissions?.() ?? [];
    const slotsUsed = missions.length;
    const cap = missionsCapFor(this.isPremium?.() ?? false);
    const atCap = slotsUsed >= cap;
    this.slotCounter.textContent = t('mission.slotsUsed', {
      used: slotsUsed,
      max: cap,
    });
    this.slotCounter.style.color = atCap ? COLORS.danger : COLORS.dim;
    this.slotCounter.style.textShadow = atCap ? GLOW : 'none';

    if (missions.length === 0) {
      this.body.appendChild(
        this.el('div', {
          color: COLORS.dim,
          textAlign: 'center',
          padding: '24px 8px',
          fontSize: '11px',
          textTransform: 'none',
          lineHeight: '1.4',
        }, t('mission.noneActive')),
      );
      return;
    }
    for (const m of missions) {
      this.body.appendChild(this.buildMissionCard(m));
    }
  }

  private buildCompletedCard(c: CompletedMissionEntry): HTMLElement {
    const card = this.el('div', {
      border: `1px solid ${COLORS.dim}`,
      background: 'rgba(0,12,0,0.55)',
      padding: '10px 12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      opacity: '0.92',
    });

    const head = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: '8px',
      flexWrap: 'wrap',
    });
    const title = this.el('div', {
      fontSize: '13px',
      textShadow: GLOW,
      letterSpacing: '2px',
      flex: '1',
      minWidth: '160px',
    }, c.title);
    head.appendChild(title);
    const badge = this.el('span', {
      fontSize: '9px',
      color: COLORS.primary,
      border: `1px solid ${COLORS.primary}`,
      padding: '1px 6px',
      letterSpacing: '2px',
    }, t('mission.completeBadge'));
    head.appendChild(badge);
    card.appendChild(head);

    if (c.description) {
      card.appendChild(
        this.el('div', {
          fontSize: '11px',
          color: COLORS.dim,
          textTransform: 'none',
          lineHeight: '1.35',
        }, c.description),
      );
    }

    const meta = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '8px',
      fontSize: '10px',
      color: COLORS.dim,
      flexWrap: 'wrap',
      marginTop: '2px',
    });
    const rewards = this.el('div', { display: 'flex', gap: '8px' });
    rewards.appendChild(this.el('span', { color: COLORS.primary },
      t('mission.rewardXp', { n: c.rewards.xp })));
    rewards.appendChild(this.el('span', { color: COLORS.secondary },
      t('mission.rewardCredits', { n: c.rewards.credits.toLocaleString() })));
    meta.appendChild(rewards);
    meta.appendChild(this.el('span', { color: COLORS.dim, textTransform: 'none' },
      this.formatRelativeTime(c.completedAt)));
    card.appendChild(meta);

    return card;
  }

  /**
   * Localized "Xm ago / 2h ago / 3d ago" helper for the completed log.
   * We intentionally fall back to a wall-clock date once we cross a
   * couple of weeks because relative phrasing past "30 days ago" stops
   * being meaningful and is harder to localize cleanly in Spanish.
   */
  private formatRelativeTime(ts: number): string {
    const ms = Date.now() - ts;
    if (ms < 60_000) return t('mission.completedJustNow');
    if (ms < 3_600_000) return t('mission.completedMinutesAgo', { n: Math.floor(ms / 60_000) });
    if (ms < 86_400_000) return t('mission.completedHoursAgo', { n: Math.floor(ms / 3_600_000) });
    if (ms < 14 * 86_400_000) return t('mission.completedDaysAgo', { n: Math.floor(ms / 86_400_000) });
    return new Date(ts).toLocaleDateString();
  }

  show(): void {
    if (this._visible) return;
    this._visible = true;
    this.root.style.display = 'flex';
    this.render();
    this.redrawTimer = setInterval(() => {
      if (this._visible) this.render();
    }, 1000);
  }

  hide(): void {
    if (!this._visible) return;
    this._visible = false;
    this.root.style.display = 'none';
    if (this.redrawTimer !== null) {
      clearInterval(this.redrawTimer);
      this.redrawTimer = null;
    }
    this.onClose?.();
  }

  refresh(): void {
    if (this._visible) this.render();
  }

  isVisible(): boolean {
    return this._visible;
  }
}
